import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import SectionsBlockEditor from "@/components/SectionsBlockEditor";
import RichTextEditor from "@/components/RichTextEditor";
import ImageField from "@/components/ImageField";

/* ═══════════════════════════════════════════
   TestEmailCard
═══════════════════════════════════════════ */
export function TestEmailCard() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await http.post("/admin/test-email", { email: email.trim() });
      setLastResult(data);
      if (data.ok && data.provider === "resend") {
        toast.success(`Email dispatched via Resend to ${data.recipient}`);
      } else if (data.ok && data.provider === "no-key-fallback") {
        toast.warning("Sent in no-key mode — RESEND_API_KEY not set in this environment");
      } else {
        toast.error("Resend rejected the send — check backend logs");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
      setLastResult({ ok: false, error: e?.response?.data?.detail || "Request failed" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid="test-email-card">
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Email Pipeline · Send Test Email</div>
      <p className="font-body text-[12px] text-[#8a857d] mb-4">
        Fires a branded one-shot email so you can confirm Resend is wired up in this environment. Use this after redeploying to Production to verify your <code className="text-[#C9A055]">RESEND_API_KEY</code> env var is set correctly without having to submit a real contact form.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="flex-1 min-w-[240px] bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-ui text-[13px] focus:border-[#C9A055] focus:outline-none"
          data-testid="test-email-input"
        />
        <button type="button" onClick={send} disabled={sending || !email}
          className="inline-flex items-center gap-2 px-5 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="send-test-email-btn"
        >{sending ? "Sending…" : "Send Test"}</button>
      </div>
      {lastResult && (
        <div className="mt-4 border border-white/10 bg-black p-3 font-ui text-[11px] tracking-[0.12em] text-[#9a9690] space-y-1" data-testid="test-email-result">
          <div>Status: <span className={lastResult.ok ? "text-[#5fc377]" : "text-[#d97a7a]"}>{lastResult.ok ? "SENT" : "FAILED"}</span></div>
          {lastResult.provider && <div>Provider: <span className="text-[#C9A055]">{lastResult.provider}</span></div>}
          {lastResult.sender && <div>Sender: <span className="text-[#C9A055]">{lastResult.sender}</span></div>}
          {lastResult.recipient && <div>Recipient: <span className="text-[#C9A055]">{lastResult.recipient}</span></div>}
          {lastResult.error && <div>Error: <span className="text-[#d97a7a]">{lastResult.error}</span></div>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   FooterEditor
═══════════════════════════════════════════ */
export function FooterEditor() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/public/content");
        const incoming = (data && data.footer) || {};
        const { FOOTER_DEFAULTS } = await import("@/components/Footer");
        setCfg({ ...FOOTER_DEFAULTS, ...incoming, columns: incoming.columns || FOOTER_DEFAULTS.columns, socials: { ...FOOTER_DEFAULTS.socials, ...(incoming.socials || {}) }, legal: incoming.legal || FOOTER_DEFAULTS.legal });
      } catch {
        const { FOOTER_DEFAULTS } = await import("@/components/Footer");
        setCfg(FOOTER_DEFAULTS);
      }
    })();
  }, []);

  if (!cfg) return <div className="border border-white/10 bg-[#0a0a0a] p-5 text-[#8a857d] font-body text-sm">Loading footer…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/content/footer", { key: "footer", value: cfg });
      toast.success("Footer saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const updateCol = (idx, patch) => {
    const next = cfg.columns.map((c, i) => i === idx ? { ...c, ...patch } : c);
    setCfg({ ...cfg, columns: next });
  };
  const updateLink = (ci, li, patch) => {
    const next = cfg.columns.map((c, i) => i === ci ? { ...c, links: c.links.map((l, j) => j === li ? { ...l, ...patch } : l) } : c);
    setCfg({ ...cfg, columns: next });
  };
  const addLink = (ci) => updateCol(ci, { links: [...(cfg.columns[ci].links || []), { label: "New Link", url: "/" }] });
  const removeLink = (ci, li) => updateCol(ci, { links: cfg.columns[ci].links.filter((_, j) => j !== li) });
  const addColumn = () => setCfg({ ...cfg, columns: [...cfg.columns, { heading: "New Column", links: [] }] });
  const removeColumn = (ci) => setCfg({ ...cfg, columns: cfg.columns.filter((_, i) => i !== ci) });
  const updateLegal = (idx, patch) => setCfg({ ...cfg, legal: cfg.legal.map((l, i) => i === idx ? { ...l, ...patch } : l) });
  const addLegal = () => setCfg({ ...cfg, legal: [...(cfg.legal || []), { label: "New Link", url: "/" }] });
  const removeLegal = (idx) => setCfg({ ...cfg, legal: cfg.legal.filter((_, i) => i !== idx) });

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-6" data-testid="footer-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">Footer</div>
          <div className="font-body text-[12px] text-[#8a857d]">Edit footer links, social URLs, tagline, blurb, and legal links. Empty social URLs auto-hide the icon.</div>
        </div>
        <button onClick={save} disabled={saving} className="btn-gold text-xs" data-testid="footer-save-btn">{saving ? "Saving…" : "Save Footer"}</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Tagline (one line per row)</div>
          <textarea rows={3} className="stc-input" value={(cfg.tagline_lines || []).join("\n")} onChange={e => setCfg({ ...cfg, tagline_lines: e.target.value.split("\n") })} data-testid="footer-tagline-input"/>
        </div>
        <div>
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Brand blurb</div>
          <textarea rows={3} className="stc-input" value={cfg.blurb || ""} onChange={e => setCfg({ ...cfg, blurb: e.target.value })} data-testid="footer-blurb-input"/>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Link Columns</div>
          <button onClick={addColumn} className="btn-outline text-[10px]" data-testid="footer-add-column">+ Column</button>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {(cfg.columns || []).map((col, ci) => (
            <div key={ci} className="border border-white/10 p-3 space-y-2" data-testid={`footer-edit-col-${ci}`}>
              <div className="flex items-center gap-2">
                <input className="stc-input flex-1" value={col.heading || ""} placeholder="Heading" onChange={e => updateCol(ci, { heading: e.target.value })} data-testid={`footer-col-heading-${ci}`}/>
                <button onClick={() => removeColumn(ci)} className="text-red-400 hover:text-red-300" aria-label="Remove column" data-testid={`footer-col-remove-${ci}`}><Trash2 size={13}/></button>
              </div>
              <div className="space-y-2">
                {(col.links || []).map((l, li) => (
                  <div key={li} className="flex gap-1.5">
                    <input className="stc-input flex-1" placeholder="Label" value={l.label || ""} onChange={e => updateLink(ci, li, { label: e.target.value })} data-testid={`footer-link-label-${ci}-${li}`}/>
                    <input className="stc-input flex-1" placeholder="/path or https://" value={l.url || ""} onChange={e => updateLink(ci, li, { url: e.target.value })} data-testid={`footer-link-url-${ci}-${li}`}/>
                    <button onClick={() => removeLink(ci, li)} className="text-red-400 hover:text-red-300 px-1" aria-label="Remove link"><Trash2 size={12}/></button>
                  </div>
                ))}
                <button onClick={() => addLink(ci)} className="btn-outline text-[10px] w-full" data-testid={`footer-add-link-${ci}`}>+ Add Link</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10 p-4 space-y-3">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Newsletter Block</div>
          <input className="stc-input" placeholder="Heading" value={cfg.newsletter_heading || ""} onChange={e => setCfg({ ...cfg, newsletter_heading: e.target.value })} data-testid="footer-news-heading"/>
          <textarea rows={2} className="stc-input" placeholder="Blurb" value={cfg.newsletter_blurb || ""} onChange={e => setCfg({ ...cfg, newsletter_blurb: e.target.value })} data-testid="footer-news-blurb"/>
        </div>
        <div className="border border-white/10 p-4 space-y-3">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Social URLs (blank = hide icon)</div>
          {["instagram", "facebook", "tiktok", "youtube"].map(k => (
            <div key={k}>
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 capitalize">{k}</div>
              <input className="stc-input" placeholder={`https://${k}.com/yourhandle`} value={cfg.socials?.[k] || ""} onChange={e => setCfg({ ...cfg, socials: { ...cfg.socials, [k]: e.target.value } })} data-testid={`footer-social-${k}-input`}/>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-white/10 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Legal Links (bottom bar)</div>
          <button onClick={addLegal} className="btn-outline text-[10px]" data-testid="footer-add-legal">+ Link</button>
        </div>
        <div className="space-y-2">
          {(cfg.legal || []).map((l, i) => (
            <div key={i} className="flex gap-1.5">
              <input className="stc-input flex-1" placeholder="Label" value={l.label || ""} onChange={e => updateLegal(i, { label: e.target.value })} data-testid={`footer-legal-label-${i}`}/>
              <input className="stc-input flex-1" placeholder="/privacy" value={l.url || ""} onChange={e => updateLegal(i, { url: e.target.value })} data-testid={`footer-legal-url-${i}`}/>
              <button onClick={() => removeLegal(i)} className="text-red-400 hover:text-red-300 px-1"><Trash2 size={12}/></button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Copyright Suffix (year auto-prepended)</div>
        <input className="stc-input" value={cfg.copyright || ""} onChange={e => setCfg({ ...cfg, copyright: e.target.value })} data-testid="footer-copyright-input"/>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   FAQEditor
═══════════════════════════════════════════ */
export function FAQEditor() {
  const [faqs, setFaqs] = useState(null);
  const [heading, setHeading] = useState({ eyebrow: "Frequently Asked", title: "QUESTIONS." });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/public/content");
        const raw = data?.faqs;
        const incoming = Array.isArray(raw) ? raw : null;
        if (incoming && incoming.length) {
          setFaqs(incoming.map(f => ({ q: f.q || f.question || "", a: f.a || f.answer || "" })));
        } else {
          const { FAQ_DEFAULTS } = await import("@/pages/FAQ");
          setFaqs(FAQ_DEFAULTS.map(f => ({ ...f })));
        }
        if (data?.faq_heading && typeof data.faq_heading === "object") setHeading(prev => ({ ...prev, ...data.faq_heading }));
      } catch {
        const { FAQ_DEFAULTS } = await import("@/pages/FAQ");
        setFaqs(FAQ_DEFAULTS.map(f => ({ ...f })));
      }
    })();
  }, []);

  if (!faqs) return <div className="border border-white/10 bg-[#0a0a0a] p-5 text-[#8a857d] font-body text-sm">Loading FAQs…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        http.put("/admin/content/faqs", { key: "faqs", value: faqs }),
        http.put("/admin/content/faq_heading", { key: "faq_heading", value: heading }),
      ]);
      toast.success("FAQs saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const updateItem = (idx, patch) => setFaqs(faqs.map((f, i) => i === idx ? { ...f, ...patch } : f));
  const remove = (idx) => setFaqs(faqs.filter((_, i) => i !== idx));
  const add = () => setFaqs([...faqs, { q: "New question", a: "" }]);
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= faqs.length) return;
    const next = faqs.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setFaqs(next);
  };

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-6" data-testid="faq-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">FAQs</div>
          <div className="font-body text-[12px] text-[#8a857d]">Edit, reorder, add, or remove questions on the public <code className="text-[#D4B07B]">/faq</code> page.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={add} className="btn-outline text-xs" data-testid="faq-add"><Plus size={12}/> Add Question</button>
          <button onClick={save} disabled={saving} className="btn-gold text-xs" data-testid="faq-save">{saving ? "Saving…" : "Save FAQs"}</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Eyebrow</div>
          <input className="stc-input" value={heading.eyebrow || ""} onChange={e => setHeading({ ...heading, eyebrow: e.target.value })} data-testid="faq-eyebrow-input"/>
        </label>
        <label className="block">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Title</div>
          <input className="stc-input" value={heading.title || ""} onChange={e => setHeading({ ...heading, title: e.target.value })} data-testid="faq-title-input"/>
        </label>
      </div>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`faq-item-${i}`}>
            <div className="flex items-center gap-2">
              <input className="stc-input flex-1" value={f.q || ""} onChange={e => updateItem(i, { q: e.target.value })} placeholder="Question" data-testid={`faq-q-input-${i}`}/>
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="btn-outline text-[10px] disabled:opacity-30" title="Move up" data-testid={`faq-up-${i}`}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === faqs.length - 1} className="btn-outline text-[10px] disabled:opacity-30" title="Move down" data-testid={`faq-down-${i}`}>↓</button>
                <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300 px-2" aria-label="Delete" data-testid={`faq-delete-${i}`}><Trash2 size={13}/></button>
              </div>
            </div>
            <textarea rows={3} className="stc-input" value={f.a || ""} onChange={e => updateItem(i, { a: e.target.value })} placeholder="Answer" data-testid={`faq-a-input-${i}`}/>
          </div>
        ))}
        {faqs.length === 0 && (
          <div className="border border-white/10 p-6 text-center text-[#8a857d] font-body text-sm">No questions yet. Add one above.</div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   PageHeadersEditor
═══════════════════════════════════════════ */
const PAGE_HEADER_SLUGS = [
  { slug: "about",          label: "About Mickey",   url: "/about" },
  { slug: "contact",        label: "Contact",        url: "/contact" },
  { slug: "shop",           label: "Shop",           url: "/shop" },
  { slug: "subscriptions",  label: "Subscriptions",  url: "/subscriptions" },
  { slug: "refer",          label: "Refer & Earn",   url: "/refer" },
  { slug: "stories",        label: "Stories",        url: "/stories" },
  { slug: "mobile-gallery", label: "Mobile Gallery", url: "/mobile-gallery" },
  { slug: "partner-apply",  label: "Become a Host",  url: "/partner-apply" },
];

export function PageHeadersEditor() {
  const [open, setOpen] = useState({});
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/public/content");
        const map = {};
        PAGE_HEADER_SLUGS.forEach(p => { map[p.slug] = data?.[`page_${p.slug}`] || {}; });
        setContent(map);
      } catch {}
    })();
  }, []);

  const update = (slug, patch) => setContent(prev => ({ ...prev, [slug]: { ...(prev[slug] || {}), ...patch } }));

  const save = async (slug) => {
    setSaving(s => ({ ...s, [slug]: true }));
    try {
      await http.put(`/admin/content/page_${slug}`, { key: `page_${slug}`, value: content[slug] || {} });
      try { (await import("@/hooks/usePageContent")).clearPageContentCache(); } catch {}
      toast.success(`Saved — ${slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(s => ({ ...s, [slug]: false })); }
  };

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid="page-headers-editor">
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">Page Headers — every other page on the site</div>
      <p className="font-body text-[12px] text-[#8a857d] mb-4">
        Override the hero (image + eyebrow + title + subtitle + WYSIWYG body) on each page. Leave a field blank to keep the page's default copy. If any field is set, the override replaces the page's hardcoded hero.
      </p>
      <div className="space-y-2">
        {PAGE_HEADER_SLUGS.map(p => {
          const c = content[p.slug] || {};
          const isOpen = open[p.slug];
          const hasAny = !!(c.eyebrow || c.title || c.subtitle || c.body || c.hero_image);
          return (
            <div key={p.slug} className="border border-white/10" data-testid={`pageheader-${p.slug}`}>
              <button
                onClick={() => setOpen(o => ({ ...o, [p.slug]: !o[p.slug] }))}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5"
                data-testid={`pageheader-toggle-${p.slug}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-[#F4F1EA]">{p.label}</span>
                  <a href={p.url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-[#C9A055] hover:underline" onClick={e => e.stopPropagation()}>{p.url} ↗</a>
                  {hasAny && <span className="font-ui text-[9px] tracking-[0.22em] uppercase text-emerald-400">Override active</span>}
                </div>
                <span className="text-[#C9A055]">{isOpen ? "▲" : "▼"}</span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-2 space-y-3 border-t border-white/10 bg-black/30">
                  <ImageField
                    label="Hero image (optional background)"
                    value={c.hero_image || ""}
                    onChange={(v) => update(p.slug, { hero_image: v })}
                    testid={`page-${p.slug}-hero-image`}
                  />
                  <div className="grid sm:grid-cols-3 gap-3">
                    <label className="block">
                      <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">Eyebrow</div>
                      <input className="stc-input" value={c.eyebrow || ""} onChange={e => update(p.slug, { eyebrow: e.target.value })} data-testid={`page-${p.slug}-eyebrow`}/>
                    </label>
                    <label className="block sm:col-span-2">
                      <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">Title (use new lines for multi-line)</div>
                      <textarea rows={2} className="stc-input" value={c.title || ""} onChange={e => update(p.slug, { title: e.target.value })} data-testid={`page-${p.slug}-title`}/>
                    </label>
                  </div>
                  <label className="block">
                    <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">Gold script subtitle</div>
                    <input className="stc-input" value={c.subtitle || ""} onChange={e => update(p.slug, { subtitle: e.target.value })} data-testid={`page-${p.slug}-subtitle`}/>
                  </label>
                  <div>
                    <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">Body (WYSIWYG — bold, italic, lists, links, inline images)</div>
                    <RichTextEditor
                      value={c.body || ""}
                      onChange={(html) => update(p.slug, { body: html })}
                      placeholder="Optional supporting copy for this page header."
                      testid={`page-${p.slug}-body`}
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                    <button onClick={() => update(p.slug, { eyebrow: "", title: "", subtitle: "", body: "", hero_image: "" })} className="btn-outline text-[10px] border-red-700 text-red-400" data-testid={`page-${p.slug}-clear`}>Clear all (restore default)</button>
                    <button onClick={() => save(p.slug)} disabled={saving[p.slug]} className="btn-gold text-[10px]" data-testid={`page-${p.slug}-save`}>{saving[p.slug] ? "Saving…" : "Save"}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   ContentCMSTab — the actual tab content
═══════════════════════════════════════════ */
export default function ContentCMSTab({ siteSettings, setSiteSettings, saveSettings }) {
  return (
    <div className="space-y-8">
      <div>
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Homepage Sections (Drag · Toggle · Edit)</div>
        <SectionsBlockEditor />
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Live Stats Baselines</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["collection_goal", "Collection Goal"],
            ["members_baseline", "Members Baseline"],
            ["qr_scans_baseline", "QR Scans Baseline"],
            ["pieces_sold_baseline", "Pieces Sold Baseline"],
          ].map(([k, l]) => (
            <div key={k}>
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">{l}</div>
              <input type="number" className="stc-input" value={siteSettings[k] ?? ""} onChange={e => setSiteSettings({...siteSettings, [k]: parseInt(e.target.value)})}/>
            </div>
          ))}
        </div>
        <button onClick={() => saveSettings({
          collection_goal: siteSettings.collection_goal, members_baseline: siteSettings.members_baseline,
          qr_scans_baseline: siteSettings.qr_scans_baseline, pieces_sold_baseline: siteSettings.pieces_sold_baseline,
        })} className="btn-gold mt-4">Save Baselines</button>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Visual Features</div>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="setting-wall-view-toggle">
          <input type="checkbox" checked={!!siteSettings.wall_view_enabled} onChange={e => saveSettings({ wall_view_enabled: e.target.checked })} className="mt-1 w-4 h-4 accent-[#C9A055]" data-testid="setting-wall-view-checkbox"/>
          <div className="flex-1">
            <div className="font-display text-[#F4F1EA] text-base">Wall View — see it at home</div>
            <p className="font-body text-[12px] text-[#8a857d] mt-1">When ON, a "Wall View" button appears in the artwork lightbox. Buyers can preview prints at real-world scale (4×6, 8×10, 11×14, 16×20, 24×36) against living-room/bedroom/office reference photos.</p>
          </div>
        </label>
        <label className="flex items-start gap-3 cursor-pointer mt-4 pt-4 border-t border-white/10" data-testid="setting-reveal-toggle">
          <input type="checkbox" checked={siteSettings.reveal_mode_enabled !== false} onChange={e => saveSettings({ reveal_mode_enabled: e.target.checked })} className="mt-1 w-4 h-4 accent-[#C9A055]" data-testid="setting-reveal-checkbox"/>
          <div className="flex-1">
            <div className="font-display text-[#F4F1EA] text-base">"What Do You See?" — interactive reveal gallery</div>
            <p className="font-body text-[12px] text-[#8a857d] mt-1">When ON, the <Link to="/what-do-you-see" target="_blank" rel="noreferrer" className="text-[#C9A055] underline">/what-do-you-see</Link> standalone reveal gallery is publicly available. Saved visitor responses appear in <Link to="/admin?tab=interpretations" className="text-[#C9A055] underline">Admin → Interpretations</Link>.</p>
          </div>
        </label>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Brand Logo (QR Flyer & Receipts)</div>
        <p className="font-body text-[12px] text-[#8a857d] mb-4">Upload your circular STC seal here. It replaces the drawn monogram on every printable QR flyer. Use a transparent PNG on a square canvas for best results. Recommended: 1000×1000 px or larger.</p>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="w-32 h-32 border border-white/10 bg-black flex items-center justify-center overflow-hidden shrink-0" data-testid="site-logo-preview">
            {siteSettings.site_logo_url ? (
              <img src={siteSettings.site_logo_url.startsWith("http") ? siteSettings.site_logo_url : `${process.env.REACT_APP_BACKEND_URL}${siteSettings.site_logo_url}`} alt="Brand logo" className="max-w-full max-h-full object-contain"/>
            ) : (
              <span className="font-ui text-[10px] tracking-[0.22em] text-[#5a5650]">No logo yet</span>
            )}
          </div>
          <div className="flex-1 min-w-[240px] space-y-2">
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase cursor-pointer transition-colors" data-testid="upload-logo-btn">
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 5 * 1024 * 1024) { toast.error("Logo must be 5 MB or smaller"); return; }
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: f.name });
                      await saveSettings({ site_logo_url: data.url });
                      toast.success("Logo uploaded — your QR flyers will use it from now on.");
                    } catch { toast.error("Upload failed"); }
                  };
                  reader.readAsDataURL(f);
                  e.target.value = "";
                }}
              />
              Upload Logo
            </label>
            {siteSettings.site_logo_url && (
              <button type="button" data-testid="remove-logo-btn"
                onClick={async () => { await saveSettings({ site_logo_url: "" }); toast.success("Logo removed — flyers will fall back to the drawn monogram."); }}
                className="ml-2 inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-[#9a9690] hover:text-[#F4F1EA] hover:border-white/40 font-ui text-[10px] tracking-[0.32em] uppercase transition-colors">Remove</button>
            )}
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#5a5650] mt-2 break-all">{siteSettings.site_logo_url || "—"}</div>
          </div>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Truth Certificate Logo</div>
        <p className="font-body text-[12px] text-[#8a857d] mb-4">Logo printed at the top of every Truth Certificate PDF (emailed to customers when orders are paid). Use the round gold STC seal on transparent or black background. If left empty we'll use the bundled default. Recommended: 600×600 px.</p>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="w-32 h-32 border border-white/10 bg-black flex items-center justify-center overflow-hidden shrink-0" data-testid="cert-logo-preview">
            {siteSettings.certificate_logo_url ? (
              <img src={siteSettings.certificate_logo_url.startsWith("http") ? siteSettings.certificate_logo_url : `${process.env.REACT_APP_BACKEND_URL}${siteSettings.certificate_logo_url}`} alt="Certificate logo" className="max-w-full max-h-full object-contain"/>
            ) : (
              <span className="font-ui text-[10px] tracking-[0.22em] text-[#5a5650]">Using default seal</span>
            )}
          </div>
          <div className="flex-1 min-w-[240px] space-y-2">
            <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase cursor-pointer transition-colors" data-testid="upload-cert-logo-btn">
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 5 * 1024 * 1024) { toast.error("Logo must be 5 MB or smaller"); return; }
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: f.name });
                      await saveSettings({ certificate_logo_url: data.url });
                      toast.success("Certificate logo updated — new certs will use it immediately.");
                    } catch { toast.error("Upload failed"); }
                  };
                  reader.readAsDataURL(f);
                  e.target.value = "";
                }}
              />
              Upload Certificate Logo
            </label>
            {siteSettings.certificate_logo_url && (
              <button type="button" data-testid="remove-cert-logo-btn"
                onClick={async () => { await saveSettings({ certificate_logo_url: "" }); toast.success("Reverted to default STC seal."); }}
                className="ml-2 inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-[#9a9690] hover:text-[#F4F1EA] hover:border-white/40 font-ui text-[10px] tracking-[0.32em] uppercase">Remove</button>
            )}
            <button type="button" data-testid="cert-preview-btn"
              onClick={async () => {
                try {
                  const { data } = await http.get("/admin/certificate-preview.pdf", { responseType: "blob" });
                  const url = URL.createObjectURL(data);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch { toast.error("Preview failed"); }
              }}
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-[#9a9690] hover:text-[#C9A055] hover:border-[#C9A055] font-ui text-[10px] tracking-[0.32em] uppercase">Preview Sample</button>
          </div>
        </div>
      </div>

      <TestEmailCard />
      <FooterEditor />
      <FAQEditor />
      <PageHeadersEditor />
    </div>
  );
}
